import { useState, useCallback, useRef } from 'react';
import { ReferralData, DeduplicationMatch } from '../types';
import { sendToCC, removeLeadLock } from 'src/services/leadManagementApi';
import { buildMergeRecordPayloadBuilder } from '../utils/buildSendToCCPayload';
import { STATUS, MAX_OPERATION_RETRIES } from 'components/LeadManagement/constants';

export const useMergeModal = (
  currentUser?: string,
  isLockingEnabled = false,
  onSendToCCLockValidationError?: (message: string) => void
) => {
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [selectedMergeRow, setSelectedMergeRow] = useState<ReferralData | null>(null);
  const [selectedMergeTarget, setSelectedMergeTarget] = useState<DeduplicationMatch | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'confirm' | 'result' | 'retry'>('result');
  const [resultMessage, setResultMessage] = useState('');
  const [resultType, setResultType] = useState<'success' | 'error'>('success');
  const [isSending, setIsSending] = useState(false);
  const mergeRetryCountRef = useRef(0);
  const lastMergeParamsRef = useRef<{
    selectedFields: string[];
    setSelectedFields: (fields: string[]) => void;
    confirmationModalMessages: any;
    onClose: () => void;
    trackingParamsData?: any;
    targetRecord?: any;
  } | null>(null);

  const handleOpenMergeModal = useCallback(
    (match: DeduplicationMatch | undefined, row: ReferralData) => {
      if (!row) return;

      setSelectedMergeRow(row);
      setSelectedMergeTarget(match ?? null);
      setMergeModalOpen(true);
    },
    []
  );

  const handleCloseMergeModal = useCallback(() => {
    setMergeModalOpen(false);
  }, []);

  const performSendToCC = useCallback(
    async (
      selectedFields: string[],
      setSelectedFields: (fields: string[]) => void,
      confirmationModalMessages: any,
      onClose: () => void,
      trackingParamsData?: any,
      targetRecord?: any
    ) => {
      if (!selectedMergeRow) return;

      setIsSending(true);

      try {
        const payload = buildMergeRecordPayloadBuilder(
          selectedMergeRow,
          String(selectedMergeTarget?.id),
          selectedFields,
          !selectedMergeTarget
            ? STATUS.NEW_RECORD
            : selectedMergeRow.status === STATUS.RETURNING_USER ||
              selectedMergeRow.dedupeStatus === STATUS.RETURNING_USER
            ? STATUS.RETURNING_USER
            : STATUS.AUTO_MERGE,
          trackingParamsData,
          targetRecord
        );

        const response = await sendToCC(payload, { isLockingEnabled, userName: currentUser });

        if (response.success) {
          setIsSending(false);
          mergeRetryCountRef.current = 0;

          // Auto-unlock after successful Send to CC (same as TableRow direct path)
          if (isLockingEnabled && currentUser && selectedMergeRow.lockedBy === currentUser) {
            const leadId = String(selectedMergeRow.leadId ?? selectedMergeRow.id);
            removeLeadLock(leadId, currentUser).catch((err) =>
              console.warn('[useMergeModal] Auto-unlock after Send to CC failed:', err?.message)
            );
          }

          const resources = response.resources || {};
          const resourceTypes = [
            { key: 'conditions', label: 'Conditions' },
            { key: 'procedures', label: 'Procedures' },
            { key: 'medications', label: 'Medications' },
            { key: 'immunizations', label: 'Immunizations' },
            { key: 'allergies', label: 'Allergies' },
            { key: 'familyMemberHistory', label: 'Family Member History' },
            { key: 'devices', label: 'Devices' },
          ];

          const failedResources: string[] = [];
          const errorMessages: string[] = [];
          let hasSkippedResources = false;

          resourceTypes.forEach(({ key, label }) => {
            const resourceData = resources[key];
            if (resourceData && resourceData !== null) {
              if (resourceData.ccError) {
                failedResources.push(label);
                errorMessages.push(resourceData.ccError);
              }
              if (resourceData.hasSkippedResources === true) {
                hasSkippedResources = true;
              }
            }
          });

          const hasResourceError = failedResources.length > 0;

          const skippedResourceMessage = hasSkippedResources
            ? 'Some Resources were skipped, check the full User profile in processed tab for more details.'
            : '';

          if (hasResourceError) {
            setResultType('success');
            const partialSuccessMessage =
              confirmationModalMessages?.ConfirmationModalPartialSuccessMessage?.value || '-';

            const failedResourcesList = failedResources.join(', ');
            const combinedErrors = errorMessages.join(', ');

            let formattedMessage = partialSuccessMessage
              .replace('[resources]', failedResourcesList)
              .replace('[ccError]', combinedErrors)
              .replace('[skippedResources]', skippedResourceMessage)
              .replace('[ccId]', response?.ccId);

            formattedMessage = formattedMessage.replace(/\n\s*\n/g, '\n').trim();

            setResultMessage(formattedMessage);
          } else {
            setResultType('success');
            let successMessage =
              confirmationModalMessages?.ConfirmationModalSuccessMessage?.value ||
              'Record merged and sent to CC successfully';
            successMessage = successMessage
              .replace('[ccId]', response?.ccId || selectedMergeTarget?.ccPatientReferenceId || '')
              .replace('[skippedResources]', skippedResourceMessage);

            successMessage = successMessage.replace(/\n\s*\n/g, '\n').trim();

            setResultMessage(successMessage);
          }
          setDialogMode('result');
          setShowDialog(true);
          setSelectedFields([]);
          onClose();
        } else {
          setIsSending(false);
          if (mergeRetryCountRef.current < MAX_OPERATION_RETRIES) {
            setResultMessage('The operation failed. Would you like to try again?');
            setDialogMode('retry');
            setShowDialog(true);
          } else {
            setResultType('error');
            setResultMessage(
              confirmationModalMessages?.ConfirmationModalErrorMessage?.value ||
                response.message ||
                'Failed to merge and send record to CC'
            );
            setDialogMode('result');
            setShowDialog(true);
            setSelectedFields([]);
            mergeRetryCountRef.current = 0;
            onClose();
          }
        }
      } catch (error: any) {
        setIsSending(false);
        const msg = typeof error?.message === 'string' ? error.message : '';
        const isClientValidation = msg && !error?.response && error instanceof Error;
        if (isClientValidation && onSendToCCLockValidationError) {
          mergeRetryCountRef.current = 0;
          onSendToCCLockValidationError(msg);
          setSelectedFields([]);
          onClose();
          return;
        }
        if (mergeRetryCountRef.current < MAX_OPERATION_RETRIES) {
          setResultMessage('The operation failed. Would you like to try again?');
          setDialogMode('retry');
          setShowDialog(true);
        } else {
          setResultType('error');
          setResultMessage(
            confirmationModalMessages?.ConfirmationModalErrorMessage?.value ||
              error?.response?.data?.message ||
              msg ||
              'Unknown error occurred'
          );
          setDialogMode('result');
          setShowDialog(true);
          setSelectedFields([]);
          mergeRetryCountRef.current = 0;
          onClose();
        }
      }
    },
    [
      selectedMergeRow,
      selectedMergeTarget,
      isLockingEnabled,
      currentUser,
      onSendToCCLockValidationError,
    ]
  );

  const handleMerge = useCallback(
    async (
      selectedFields: string[],
      setSelectedFields: (fields: string[]) => void,
      confirmationModalMessages: any,
      onClose: () => void,
      trackingParamsData?: any,
      targetRecord?: any
    ) => {
      lastMergeParamsRef.current = {
        selectedFields,
        setSelectedFields,
        confirmationModalMessages,
        onClose,
        trackingParamsData,
        targetRecord,
      };
      await performSendToCC(
        selectedFields,
        setSelectedFields,
        confirmationModalMessages,
        onClose,
        trackingParamsData,
        targetRecord
      );
    },
    [performSendToCC]
  );

  const handleRetryMerge = useCallback(async () => {
    const params = lastMergeParamsRef.current;
    if (!params) return;
    setShowDialog(false);
    mergeRetryCountRef.current += 1;
    await performSendToCC(
      params.selectedFields,
      params.setSelectedFields,
      params.confirmationModalMessages,
      params.onClose,
      params.trackingParamsData,
      params.targetRecord
    );
  }, [performSendToCC]);

  return {
    mergeModalOpen,
    selectedMergeRow,
    selectedMergeTarget,
    handleOpenMergeModal,
    handleCloseMergeModal,
    handleMerge,
    handleRetryMerge,
    showDialog,
    dialogMode,
    resultMessage,
    resultType,
    isSending,
    setShowDialog,
  };
};
